import React, { useEffect, useState } from 'react';
import { useForm, Controller, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import {
  Loader2,
  RefreshCw,
  Edit,
  AlertTriangle,
  Plus,
  Trash2,
  Copy,
} from 'lucide-react';
import { clinicParticipatesInKinnser } from '../../utils/clinicKeyUtils';
import {
  listClinicPhysiciansAdminClinicPhysiciansGet as listClinicPhysicians,
  createClinicPhysicianAdminClinicPhysiciansPost as createClinicPhysician,
  updateClinicPhysicianAdminClinicPhysiciansClinicPhysicianIdPut as updateClinicPhysician,
  deleteClinicPhysicianAdminClinicPhysiciansClinicPhysicianIdDelete as deleteClinicPhysician,
  duplicateClinicPhysiciansAdminClinicPhysiciansDuplicatePost as duplicateClinicPhysicians,
} from '../../services/api/sdk.gen';
import type {
  AgencyClinicResponse,
  ClinicPhysicianResponse,
  OrderDeliveryPreference,
} from '../../services/api/types.gen';
import { NpiInput } from '../../components/NpiInput';
import TablePaginationFooter from '../../components/TablePaginationFooter';
import { validateNpi } from '../../utils/npiValidation';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { ClinicSelect } from '../../components/clinic/ClinicSelect';
import {
  ORDER_DELIVERY_LABELS,
  ORDER_DELIVERY_VALUES,
} from '../../utils/orderDeliveryPreference';

const physicianFormSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  suffix: z.string(),
  npi: z.string().superRefine((value, ctx) => {
    if (value.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'NPI is required' });
      return;
    }
    const error = validateNpi(value);
    if (error) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
    }
  }),
  address: z.string(),
  phone: z.string(),
  fax: z.string(),
  email: z.union([z.literal(''), z.string().email('Invalid email address')]),
  pecos: z.boolean(),
  order_delivery_preference: z.enum(ORDER_DELIVERY_VALUES),
  comment: z.string(),
});

type PhysicianFormData = z.infer<typeof physicianFormSchema>;

function firstPhysicianFormErrorMessage(
  errors: FieldErrors<PhysicianFormData>
): string | undefined {
  for (const err of Object.values(errors)) {
    if (err && typeof err === 'object' && 'message' in err && err.message) {
      return String(err.message);
    }
  }
  return undefined;
}

const PHYSICIAN_FORM_DEFAULTS: PhysicianFormData = {
  first_name: '',
  last_name: '',
  suffix: '',
  npi: '',
  address: '',
  phone: '',
  fax: '',
  email: '',
  pecos: false,
  order_delivery_preference: 'fax',
  comment: '',
};

interface PhysiciansSectionProps {
  clinics: AgencyClinicResponse[];
  isSuperUser: boolean;
  isRogerEmrEnabled: boolean;
  /** Incremented by the parent to force a reload (e.g. after a clinic info sync). */
  refreshToken?: number;
}

const PHYSICIAN_PAGE_SIZE_OPTIONS = [10, 25, 50];

const PhysiciansSection: React.FC<PhysiciansSectionProps> = ({
  clinics,
  isSuperUser,
  isRogerEmrEnabled,
  refreshToken = 0,
}) => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [isLoadingPhysicians, setIsLoadingPhysicians] = useState(false);
  const [physicianFilterClinicId, setPhysicianFilterClinicId] = useState<
    string | null
  >(null);
  const [physicianSearch, setPhysicianSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PHYSICIAN_PAGE_SIZE_OPTIONS[0]);

  const [isPhysicianModalOpen, setIsPhysicianModalOpen] = useState(false);
  const [physicianModalMode, setPhysicianModalMode] = useState<
    'create' | 'edit'
  >('create');
  const [editingPhysicianId, setEditingPhysicianId] = useState<string | null>(
    null
  );
  const [physicianSubmitError, setPhysicianSubmitError] = useState<
    string | null
  >(null);
  const physicianForm = useForm<PhysicianFormData>({
    resolver: zodResolver(physicianFormSchema),
    defaultValues: PHYSICIAN_FORM_DEFAULTS,
    mode: 'onSubmit',
  });
  const {
    register: registerPhysicianField,
    handleSubmit: handlePhysicianFormSubmit,
    reset: resetPhysicianForm,
    control: physicianFormControl,
    formState: { errors: physicianFormErrors, isSubmitting: isSavingPhysician },
  } = physicianForm;
  const [deletingPhysicianId, setDeletingPhysicianId] = useState<string | null>(
    null
  );
  const [isPhysicianDeleteDialogOpen, setIsPhysicianDeleteDialogOpen] =
    useState(false);
  const [physicianPendingDeleteId, setPhysicianPendingDeleteId] = useState<
    string | null
  >(null);

  // Duplicate-physicians modal state (super-user only)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateSourceClinicId, setDuplicateSourceClinicId] = useState('');
  const [duplicateDestClinicId, setDuplicateDestClinicId] = useState('');
  const [isDuplicatingPhysicians, setIsDuplicatingPhysicians] = useState(false);

  // Copy only into non-Kinnser clinics (Kinnser physicians stay sync-owned).
  const rogerEmrClinics = clinics.filter(c => !clinicParticipatesInKinnser(c));
  const canCopyPhysiciansToRogerClinic =
    clinics.length >= 2 && rogerEmrClinics.length >= 1;

  // Default physician filter to first clinic
  useEffect(() => {
    if (clinics.length > 0) {
      setPhysicianFilterClinicId(prev => prev ?? clinics[0].id);
    }
  }, [clinics]);

  const loadPhysicians = async (clinicId: string) => {
    setIsLoadingPhysicians(true);
    try {
      const result = await listClinicPhysicians({
        query: { clinic_id: clinicId },
        throwOnError: true,
      });
      setPhysicians(result.data ?? []);
    } catch (e) {
      console.error('failed to load physicians', e);
      showError('Failed to load physicians');
    } finally {
      setIsLoadingPhysicians(false);
    }
  };

  // Re-fetch physicians when the clinic filter changes or the parent bumps
  // refreshToken (e.g. after a clinic info sync creates/updates physicians).
  useEffect(() => {
    if (physicianFilterClinicId) {
      loadPhysicians(physicianFilterClinicId);
    } else {
      setPhysicians([]);
    }
  }, [physicianFilterClinicId, refreshToken]);

  const search = physicianSearch.trim().toLowerCase();
  const filteredPhysicians = physicians.filter(physician => {
    if (search === '') return true;
    const fullName = [physician.first_name, physician.last_name]
      .join(' ')
      .toLowerCase();
    return (
      fullName.includes(search) ||
      (physician.suffix ?? '').toLowerCase().includes(search) ||
      (physician.npi ?? '').toLowerCase().includes(search) ||
      (physician.phone ?? '').toLowerCase().includes(search)
    );
  });

  // Back to the first page whenever the search or clinic changes.
  useEffect(() => {
    setPageIndex(0);
  }, [physicianSearch, physicianFilterClinicId]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredPhysicians.length / pageSize)
  );
  // Clamp the page if the filtered set shrank below the current page.
  useEffect(() => {
    if (pageIndex > 0 && pageIndex > pageCount - 1) {
      setPageIndex(pageCount - 1);
    }
  }, [pageIndex, pageCount]);

  const pagedPhysicians = filteredPhysicians.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  const handleCreatePhysician = () => {
    if (clinics.length === 0) return;
    setPhysicianModalMode('create');
    setEditingPhysicianId(null);
    setPhysicianSubmitError(null);
    resetPhysicianForm(PHYSICIAN_FORM_DEFAULTS);
    setIsPhysicianModalOpen(true);
  };

  const handleEditPhysician = (physician: ClinicPhysicianResponse) => {
    setPhysicianModalMode('edit');
    setEditingPhysicianId(physician.id);
    setPhysicianSubmitError(null);
    resetPhysicianForm({
      first_name: physician.first_name,
      last_name: physician.last_name,
      suffix: physician.suffix ?? '',
      npi: physician.npi ?? '',
      address: physician.address ?? '',
      phone: physician.phone ?? '',
      fax: physician.fax ?? '',
      email: physician.email ?? '',
      pecos: physician.pecos ?? false,
      order_delivery_preference: physician.order_delivery_preference,
      comment: physician.comment ?? '',
    });
    setIsPhysicianModalOpen(true);
  };

  const handleSavePhysician = handlePhysicianFormSubmit(
    async values => {
      if (!physicianFilterClinicId) return;
      setPhysicianSubmitError(null);
      try {
        const body = {
          first_name: values.first_name.trim(),
          last_name: values.last_name.trim(),
          suffix: values.suffix.trim() || null,
          npi: values.npi.trim(),
          address: values.address.trim() || null,
          phone: values.phone.trim() || null,
          fax: values.fax.trim() || null,
          email: values.email.trim() || null,
          pecos: values.pecos,
          order_delivery_preference: values.order_delivery_preference,
          comment: values.comment.trim() || null,
        };
        if (physicianModalMode === 'create') {
          await createClinicPhysician({
            body: { ...body, clinic_id: physicianFilterClinicId },
            throwOnError: true,
          });
          showSuccess('Physician created successfully');
        } else {
          if (!editingPhysicianId) return;
          await updateClinicPhysician({
            path: { clinic_physician_id: editingPhysicianId },
            body,
            throwOnError: true,
          });
          showSuccess('Physician updated successfully');
        }
        setIsPhysicianModalOpen(false);
        await loadPhysicians(physicianFilterClinicId);
      } catch (e) {
        console.error('failed to save physician', e);
        // Surface a backend business message (e.g. duplicate NPI 409) when one
        // is provided; otherwise fall back to a generic message.
        const fallback =
          physicianModalMode === 'create'
            ? 'Unable to create physician. Please review the form and try again.'
            : 'Unable to update physician. Please review the form and try again.';
        const message = getBusinessErrorMessage(e) ?? fallback;
        setPhysicianSubmitError(message);
        showError(message);
      }
    },
    errors => {
      const msg =
        firstPhysicianFormErrorMessage(errors) ??
        'Please fix the errors in the form.';
      showError(msg);
    }
  );

  const handleCancelPhysicianModal = () => {
    setIsPhysicianModalOpen(false);
    setEditingPhysicianId(null);
    setPhysicianSubmitError(null);
  };

  const handleOpenDuplicatePhysicians = () => {
    setDuplicateSourceClinicId('');
    setDuplicateDestClinicId('');
    setIsDuplicateModalOpen(true);
  };

  const handleConfirmDuplicatePhysicians = async () => {
    if (!duplicateSourceClinicId || !duplicateDestClinicId) {
      showError('Please select both a source and destination clinic');
      return;
    }
    if (duplicateSourceClinicId === duplicateDestClinicId) {
      showError('Source and destination clinics must be different');
      return;
    }
    setIsDuplicatingPhysicians(true);
    try {
      const { data: result } = await duplicateClinicPhysicians({
        body: {
          source_clinic_id: duplicateSourceClinicId,
          destination_clinic_id: duplicateDestClinicId,
        },
        throwOnError: true,
      });
      showSuccess(
        `Copied ${result.created_count} physician${
          result.created_count === 1 ? '' : 's'
        }` +
          (result.skipped_count > 0
            ? ` (${result.skipped_count} skipped — NPI already present or missing)`
            : '')
      );
      setIsDuplicateModalOpen(false);
      if (physicianFilterClinicId === duplicateDestClinicId) {
        await loadPhysicians(physicianFilterClinicId);
      }
    } catch (e) {
      console.error('failed to copy physicians', e);
      showError(
        getBusinessErrorMessage(e) ??
          'Failed to copy physicians. Please try again.'
      );
    } finally {
      setIsDuplicatingPhysicians(false);
    }
  };

  const handleDeletePhysician = (physicianId: string) => {
    setPhysicianPendingDeleteId(physicianId);
    setIsPhysicianDeleteDialogOpen(true);
  };

  const confirmDeletePhysician = async () => {
    if (!physicianPendingDeleteId) return;
    const physicianId = physicianPendingDeleteId;
    setDeletingPhysicianId(physicianId);
    try {
      await deleteClinicPhysician({
        path: { clinic_physician_id: physicianId },
        throwOnError: true,
      });
      showSuccess('Physician deleted');
      setIsPhysicianDeleteDialogOpen(false);
      setPhysicianPendingDeleteId(null);
      if (physicianFilterClinicId)
        await loadPhysicians(physicianFilterClinicId);
    } catch (e) {
      console.error('failed to delete physician', e);
      showError('Failed to delete physician');
    } finally {
      setDeletingPhysicianId(null);
    }
  };

  return (
    <>
      {/* Physicians Section */}
      <Card>
        <CardHeader>
          <div className='flex justify-between items-start'>
            <div>
              <CardTitle>Physicians</CardTitle>
              <CardDescription>
                Manage physicians configured for your agency
              </CardDescription>
            </div>
            <div className='flex gap-2'>
              {isRogerEmrEnabled &&
                isSuperUser &&
                canCopyPhysiciansToRogerClinic && (
                  <Button
                    onClick={handleOpenDuplicatePhysicians}
                    variant='outline'
                  >
                    <Copy className='mr-2 h-4 w-4' />
                    Copy to Clinic...
                  </Button>
                )}
              {clinics.length === 0 ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button variant='outline' disabled>
                          <Plus className='mr-2 h-4 w-4' />
                          New Physician
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create a clinic first before adding physicians</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Button onClick={handleCreatePhysician} variant='outline'>
                  <Plus className='mr-2 h-4 w-4' />
                  New Physician
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-4'>
          {clinics.length === 0 ? (
            <div className='rounded-md border p-6 text-center text-muted-foreground text-sm'>
              Create a clinic first before adding physicians.
            </div>
          ) : (
            <>
              <div className='flex flex-col lg:flex-row gap-4'>
                <div className='flex-1'>
                  <Input
                    placeholder='Search physicians...'
                    value={physicianSearch}
                    onChange={e => setPhysicianSearch(e.target.value)}
                    className='w-full'
                  />
                </div>
                <div className='w-full lg:w-64'>
                  <ClinicSelect
                    clinics={clinics}
                    value={physicianFilterClinicId ?? ''}
                    onChange={e => setPhysicianFilterClinicId(e.target.value)}
                    placeholder='Select a clinic'
                  />
                </div>
                <Button
                  onClick={() =>
                    physicianFilterClinicId &&
                    loadPhysicians(physicianFilterClinicId)
                  }
                  disabled={isLoadingPhysicians || !physicianFilterClinicId}
                  variant='outline'
                >
                  {isLoadingPhysicians ? (
                    <Loader2 className='h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                </Button>
              </div>

              <div className='rounded-md border'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>NPI</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Fax</TableHead>
                      <TableHead>Delivery Preference</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingPhysicians ? (
                      <TableRow>
                        <TableCell colSpan={6} className='text-center py-8'>
                          <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                          <p className='text-sm text-muted-foreground mt-2'>
                            Loading physicians...
                          </p>
                        </TableCell>
                      </TableRow>
                    ) : filteredPhysicians.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className='text-center py-8 text-muted-foreground'
                        >
                          {!physicianFilterClinicId
                            ? 'Select a clinic to view physicians.'
                            : physicians.length > 0
                              ? 'No physicians match your search.'
                              : 'No physicians found for this clinic. Click "New Physician" to create one.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedPhysicians.map(physician => (
                        <TableRow key={physician.id}>
                          <TableCell className='font-medium'>
                            {physician.first_name} {physician.last_name}
                            {physician.suffix && (
                              <span className='text-muted-foreground'>
                                {', '}
                                {physician.suffix}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {physician.npi ? (
                              <Badge variant='secondary'>{physician.npi}</Badge>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {physician.phone ? (
                              <span className='text-sm'>{physician.phone}</span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {physician.fax ? (
                              <span className='text-sm'>{physician.fax}</span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant='outline'>
                              {ORDER_DELIVERY_LABELS[
                                physician.order_delivery_preference
                              ] ?? physician.order_delivery_preference}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className='flex gap-1'>
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() => handleEditPhysician(physician)}
                              >
                                <Edit className='h-4 w-4' />
                              </Button>
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() =>
                                  handleDeletePhysician(physician.id)
                                }
                                disabled={deletingPhysicianId === physician.id}
                                className='text-red-600 hover:text-red-700 hover:bg-red-50'
                              >
                                {deletingPhysicianId === physician.id ? (
                                  <Loader2 className='h-4 w-4 animate-spin' />
                                ) : (
                                  <Trash2 className='h-4 w-4' />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <TablePaginationFooter
                pageIndex={pageIndex}
                pageCount={pageCount}
                totalCount={filteredPhysicians.length}
                pageSize={pageSize}
                pageSizeOptions={PHYSICIAN_PAGE_SIZE_OPTIONS}
                canPreviousPage={pageIndex > 0}
                canNextPage={pageIndex < pageCount - 1}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setPageIndex(0);
                }}
                onPreviousPage={() =>
                  setPageIndex(prev => Math.max(0, prev - 1))
                }
                onNextPage={() =>
                  setPageIndex(prev => Math.min(pageCount - 1, prev + 1))
                }
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Physician Create/Edit Modal */}
      <Dialog
        open={isPhysicianModalOpen}
        onOpenChange={setIsPhysicianModalOpen}
      >
        <DialogContent className='sm:max-w-[600px] max-h-[85vh] flex flex-col'>
          <form
            className='flex flex-col flex-1 min-h-0 gap-0'
            onSubmit={handleSavePhysician}
          >
            <DialogHeader>
              <DialogTitle>
                {physicianModalMode === 'create'
                  ? 'New Physician'
                  : 'Edit Physician'}
              </DialogTitle>
              <DialogDescription>
                {physicianModalMode === 'create'
                  ? 'Add a new physician to this clinic.'
                  : 'Update physician details.'}
              </DialogDescription>
            </DialogHeader>
            <div className='grid gap-4 py-4 overflow-y-auto flex-1 pr-1'>
              {physicianSubmitError && (
                <Alert variant='destructive'>
                  <AlertDescription className='flex gap-2 items-center'>
                    <AlertTriangle className='h-4 w-4 shrink-0' />
                    {physicianSubmitError}
                  </AlertDescription>
                </Alert>
              )}
              <div className='grid grid-cols-2 gap-4 items-start'>
                <div className='grid gap-2'>
                  <label className='text-sm font-medium'>First Name *</label>
                  <Input
                    inputSize='default'
                    {...registerPhysicianField('first_name')}
                    aria-invalid={
                      physicianFormErrors.first_name ? 'true' : 'false'
                    }
                  />
                  {physicianFormErrors.first_name && (
                    <p className='text-xs text-red-600'>
                      {physicianFormErrors.first_name.message}
                    </p>
                  )}
                </div>
                <div className='grid gap-2'>
                  <label className='text-sm font-medium'>Last Name *</label>
                  <Input
                    inputSize='default'
                    {...registerPhysicianField('last_name')}
                    aria-invalid={
                      physicianFormErrors.last_name ? 'true' : 'false'
                    }
                  />
                  {physicianFormErrors.last_name && (
                    <p className='text-xs text-red-600'>
                      {physicianFormErrors.last_name.message}
                    </p>
                  )}
                </div>
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Suffix</label>
                <Input
                  inputSize='default'
                  {...registerPhysicianField('suffix')}
                  placeholder='e.g. MD, DO'
                />
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>NPI *</label>
                <Controller
                  control={physicianFormControl}
                  name='npi'
                  render={({ field }) => (
                    <NpiInput
                      ref={field.ref}
                      inputSize='default'
                      name={field.name}
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder='10-digit NPI'
                      externalError={physicianFormErrors.npi?.message}
                      aria-invalid={physicianFormErrors.npi ? 'true' : 'false'}
                    />
                  )}
                />
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>
                  Order Delivery Preference *
                </label>
                <Controller
                  control={physicianFormControl}
                  name='order_delivery_preference'
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={value =>
                        field.onChange(value as OrderDeliveryPreference)
                      }
                    >
                      <SelectTrigger selectSize='default'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORDER_DELIVERY_VALUES.map(value => (
                          <SelectItem key={value} value={value}>
                            {ORDER_DELIVERY_LABELS[value]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Phone</label>
                <Input
                  inputSize='default'
                  {...registerPhysicianField('phone')}
                />
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Fax</label>
                <Input inputSize='default' {...registerPhysicianField('fax')} />
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Email</label>
                <Input
                  inputSize='default'
                  type='email'
                  {...registerPhysicianField('email')}
                  placeholder='physician@example.com'
                  aria-invalid={physicianFormErrors.email ? 'true' : 'false'}
                />
                {physicianFormErrors.email && (
                  <p className='text-xs text-red-600'>
                    {physicianFormErrors.email.message}
                  </p>
                )}
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Address</label>
                <Input
                  inputSize='default'
                  {...registerPhysicianField('address')}
                />
              </div>
              <div className='flex items-center gap-3'>
                <Controller
                  control={physicianFormControl}
                  name='pecos'
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <label className='text-sm font-medium'>PECOS Enrolled</label>
              </div>
              <div className='grid gap-2'>
                <label className='text-sm font-medium'>Comment</label>
                <Input
                  inputSize='default'
                  {...registerPhysicianField('comment')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancelPhysicianModal}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSavingPhysician}>
                {isSavingPhysician && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                {physicianModalMode === 'create'
                  ? 'Create Physician'
                  : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Physicians Modal */}
      {isRogerEmrEnabled && isSuperUser && (
        <Dialog
          open={isDuplicateModalOpen}
          onOpenChange={setIsDuplicateModalOpen}
        >
          <DialogContent className='sm:max-w-[480px]'>
            <DialogHeader>
              <DialogTitle>Copy Physicians to Clinic</DialogTitle>
              <DialogDescription>
                Copy every physician from one clinic onto a Roger-only clinic
                (not Kinnser-linked).
              </DialogDescription>
            </DialogHeader>
            <div className='flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-xs text-muted-foreground'>
              <AlertTriangle className='h-4 w-4 shrink-0 mt-0.5' />
              <span>
                This creates Roger-managed physician copies, not WellSky-synced
                rows. WellSky sync will not create or update them — use clinic
                info sync to refresh physicians on Kinnser-linked clinics.
                Physicians with a matching NPI on the destination — or without
                an NPI — are skipped.
              </span>
            </div>
            <div className='space-y-4 py-2'>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>From clinic</label>
                <ClinicSelect
                  clinics={clinics}
                  value={duplicateSourceClinicId}
                  onChange={e => setDuplicateSourceClinicId(e.target.value)}
                  placeholder='Select source clinic'
                />
              </div>
              <div className='space-y-1.5'>
                <label className='text-sm font-medium'>To clinic</label>
                <ClinicSelect
                  clinics={rogerEmrClinics.filter(
                    c => c.id !== duplicateSourceClinicId
                  )}
                  value={duplicateDestClinicId}
                  onChange={e => setDuplicateDestClinicId(e.target.value)}
                  placeholder='Select destination clinic'
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setIsDuplicateModalOpen(false)}
                disabled={isDuplicatingPhysicians}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDuplicatePhysicians}
                disabled={
                  isDuplicatingPhysicians ||
                  !duplicateSourceClinicId ||
                  !duplicateDestClinicId
                }
              >
                {isDuplicatingPhysicians && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                Copy Physicians
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Physician Delete Confirmation Dialog */}
      <Dialog
        open={isPhysicianDeleteDialogOpen}
        onOpenChange={open => {
          setIsPhysicianDeleteDialogOpen(open);
          if (!open) setPhysicianPendingDeleteId(null);
        }}
      >
        <DialogContent className='sm:max-w-[400px]'>
          <DialogHeader>
            <DialogTitle>Delete Physician</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this physician? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setIsPhysicianDeleteDialogOpen(false);
                setPhysicianPendingDeleteId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={confirmDeletePhysician}
              disabled={deletingPhysicianId !== null}
            >
              {deletingPhysicianId !== null && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PhysiciansSection;
